import { useMemo } from 'react';

import { Box, Flex, Grid, HStack, Link, Spacer, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { StrapiImage } from '@/components/atoms';
import { SectionWrapper } from '@/components/wrappers';
import { GetMenuQuery } from '@/graphql/generated';
import { LOCALES } from '@/utils/constants';
import { GeneralPath, footerBottomBar } from '@/utils/models';

import { BottomBar } from './BottomBar';

export const Footer = ({ menu }: { menu: GetMenuQuery['menu'] }) => {
  const { t } = useTranslation(LOCALES.COMMON);
  const copyright = `${t('footer.copyright.0')} ${new Date().getFullYear()} ${t('footer.copyright.1')}`;
  const data = useMemo(() => menu?.data?.attributes, [menu]);
  const linksByCategory = useMemo(() => {
    if (!data) return null;

    const getLinksForCategory = (categoryTitle: string) => {
      const category = data.menuItem?.find((item) => item?.menuCategory?.title === categoryTitle);
      return category?.menuCategory?.menuSubcategories?.map((sub, i) =>
        sub ? (
          <Box key={`${categoryTitle}-${i}`}>
            <Link href={sub.url ?? undefined}>{sub.title}</Link>
          </Box>
        ) : null,
      );
    };

    return {
      services: getLinksForCategory('Services'),
      industries: getLinksForCategory('Industries'),
    };
  }, [data]);

  if (!data) return null;

  return (
    <SectionWrapper bg="surface.500" py={10} color="text.300" justifyContent="center">
      <Flex direction="column" maxW="1920px" mx="auto" w="full">
        <Flex
          direction="column"
          gap="64px"
          alignItems="start"
          paddingX={{ base: 5, sm: 6, lg: 20, '3xl': 24 }}
          mb="100px"
          pt="100px"
        >
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={10} alignItems="start" w="full">
            {/* Footer logo & heading */}
            <VStack align="start">
              {data.menuHeading && (
                <Box w="360px">
                  <Text as="h2" variant="component-heading-large/2xl" maxW="285px" color="white">
                    {data.menuHeading}
                  </Text>
                </Box>
              )}
            </VStack>

            {/* Company Links */}
            <VStack align="start" spacing={6}>
              <Text variant="text-xs/lineHeight-4/font-medium/0.75rem" mb={-2} color="white">
                Company
              </Text>
              {data.menuItem
                ?.filter((item) => item?.menuCategory?.url)
                .map((item, i) => {
                  const cat = item?.menuCategory;
                  if (!cat) return null;
                  return (
                    <Box key={`company-${i}`}>
                      <Link href={cat.url ?? undefined}>{cat.title}</Link>
                    </Box>
                  );
                })}
            </VStack>

            {/* Services */}
            <VStack align="start" spacing={6}>
              <Text variant="text-xs/lineHeight-4/font-medium/0.75rem" mb={-2} color="white">
                Services
              </Text>
              {linksByCategory?.services}
            </VStack>

            {/* Industries */}
            <VStack align="start" spacing={5}>
              <Text variant="text-xs/lineHeight-4/font-medium/0.75rem" mb={-2} color="white">
                Industries
              </Text>
              {linksByCategory?.industries}
            </VStack>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={10} alignItems="stretch" w="full">
            {/* Footer logo & main info */}
            <VStack align="start" spacing={2} w="360px">
              {data.footerLogo?.data?.attributes && (
                <Box w="auto" h="26px" mb={10}>
                  <Link href={GeneralPath.HOME}>
                    <StrapiImage image={data.footerLogo} alt="startup house warsaw" />
                  </Link>
                </Box>
              )}
              {data.mainInfo && (
                <Box>
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw, rehypeSanitize]}
                    components={{
                      p: ({ children }) => (
                        <Text variant="text-xs/lineHeight-4/font-normal" fontWeight="500" mb={1}>
                          {children}
                        </Text>
                      ),
                      strong: ({ children }) => (
                        <Text variant="text-xs/lineHeight-4/font-normal" fontWeight="700" mb={1} as="strong">
                          {children}
                        </Text>
                      ),
                    }}
                  >
                    {data.mainInfo}
                  </ReactMarkdown>
                </Box>
              )}
            </VStack>

            {/* Contact & Social */}
            <VStack align="start">
              <VStack align="start">
                <Text variant="text-xs/lineHeight-4/font-medium/0.75rem" pb={4} color="white">
                  Contact Us
                </Text>
                {data.contactInfo && (
                  <Box fontSize="sm">
                    <ReactMarkdown
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      components={{
                        p: ({ children }) => (
                          <Text variant="text-md/lineHeight-6/font-medium" fontWeight="400">
                            {children}
                          </Text>
                        ),
                        strong: ({ children }) => (
                          <Text variant="text-md/lineHeight-6/font-medium" fontWeight="600" as="strong">
                            {children}
                          </Text>
                        ),
                      }}
                    >
                      {data.contactInfo}
                    </ReactMarkdown>
                  </Box>
                )}
              </VStack>

              <Spacer />

              <VStack align="start" spacing={2} w="full">
                <Text variant="text-xs/lineHeight-4/font-medium/0.75rem" color="white">
                  Follow Us
                </Text>
                <HStack spacing={4}>
                  {data.socialLogos?.map((social, index) =>
                    social?.icon?.data?.attributes?.url ? (
                      <Link key={`social-${index}`} href={social.url || '#'} isExternal>
                        <Box w="24px" h="24px">
                          <StrapiImage
                            image={social.icon}
                            alt={`Follow us on ${social.icon.data.attributes.alternativeText}`}
                          />
                        </Box>
                      </Link>
                    ) : null,
                  )}
                </HStack>
              </VStack>
            </VStack>

            {/* Awards */}
            <HStack spacing={3} wrap="wrap" align="end">
              {data.awards?.data?.map((award, index) =>
                award?.attributes?.url ? (
                  <Box
                    key={`award-${index}`}
                    h="75px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    <StrapiImage
                      image={{
                        data: {
                          attributes: award.attributes,
                        },
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                      }}
                    />
                  </Box>
                ) : null,
              )}
            </HStack>
          </Grid>
        </Flex>

        <BottomBar bottomBarData={footerBottomBar} copyright={copyright} />
      </Flex>
    </SectionWrapper>
  );
};
